import { CloudUpload, TrendingDown, TrendingUp } from '@mui/icons-material';
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    CircularProgress,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    Link,
    MenuItem,
    Paper,
    Select,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../api';
import QuotaBlockedNotice, { useQuotaBlocked } from '../../components/subscription/QuotaBlockedNotice';
import PageTutorialButtons from '../../components/tutorials/PageTutorialButtons';
import { formatCurrencyInCents, formatCurrencyInFloat } from '../../helpers/formatters';
import {
    formatSettlementDate,
    parseSettlementFile,
    settlementDateRange,
    summarizeEntries,
} from '../../helpers/settlementImport';
import {
    buildReconciliationRows,
    buildTransactionPayloadFromEntry,
    fetchAllPages,
} from '../../helpers/settlementReconcile';

const ACCEPTED_EXTENSIONS = '.csv,.ofx,.ofd';

function statusColor(status) {
    switch (status) {
        case 'matched_order':
            return 'success';
        case 'already_reconciled':
            return 'info';
        case 'matched_transaction':
            return 'warning';
        case 'no_order':
        case 'orphan':
            return 'default';
        case 'skipped':
            return 'default';
        default:
            return 'default';
    }
}

/** 0 = entradas com tipo, 1 = saídas com tipo, 2 = sem tipo. */
function settlementRowGroup(row) {
    if (!row?.entry?.importable) {
        return 2;
    }
    if (row.entry.direction === 'income') {
        return 0;
    }
    return 1;
}

function sortSettlementRows(rows) {
    return [...(rows || [])].sort((a, b) => {
        const dateA = a.entry?.saleDateKey || a.entry?.transactionDate || '';
        const dateB = b.entry?.saleDateKey || b.entry?.transactionDate || '';
        if (dateA !== dateB) {
            return String(dateA).localeCompare(String(dateB));
        }
        return String(a.rowKey || '').localeCompare(String(b.rowKey || ''));
    });
}

function groupSettlementRows(rows) {
    const sorted = sortSettlementRows(rows);
    const income = [];
    const outcome = [];
    const unidentified = [];
    sorted.forEach((row) => {
        const group = settlementRowGroup(row);
        if (group === 0) {
            income.push(row);
        } else if (group === 1) {
            outcome.push(row);
        } else {
            unidentified.push(row);
        }
    });
    return { income, outcome, unidentified };
}

function SettlementImportPage() {
    const navigate = useNavigate();
    const inputRef = useRef(null);
    const [fileName, setFileName] = useState('');
    const [sourceLabel, setSourceLabel] = useState('');
    const [entries, setEntries] = useState([]);
    const [rows, setRows] = useState([]);
    const [dateRange, setDateRange] = useState(null);
    const [error, setError] = useState('');
    const [isParsing, setIsParsing] = useState(false);
    const [isLoadingMatches, setIsLoadingMatches] = useState(false);
    const [isReconciling, setIsReconciling] = useState(false);
    const quotaBlocked = useQuotaBlocked('reconciliations');
    const [hideReconciled, setHideReconciled] = useState(true);

    const summary = useMemo(() => summarizeEntries(entries), [entries]);
    const reconciledCount = useMemo(
        () => rows.filter((row) => row.status === 'already_reconciled').length,
        [rows],
    );
    const visibleRows = useMemo(() => {
        if (!hideReconciled) {
            return rows;
        }
        return rows.filter((row) => row.status !== 'already_reconciled');
    }, [rows, hideReconciled]);
    const rowGroups = useMemo(() => groupSettlementRows(visibleRows), [visibleRows]);

    const resetResult = () => {
        setEntries([]);
        setRows([]);
        setDateRange(null);
        setSourceLabel('');
        setFileName('');
        setError('');
    };

    const updateRow = (rowKey, patch) => {
        setRows((current) => current.map((row) => (
            row.rowKey === rowKey ? { ...row, ...patch } : row
        )));
    };

    const loadMatches = async (parsedEntries) => {
        const range = settlementDateRange(parsedEntries);
        setDateRange(range);
        if (!range) {
            setRows(buildReconciliationRows(parsedEntries, [], []));
            return;
        }

        setIsLoadingMatches(true);
        try {
            const authIds = [...new Set(
                (parsedEntries || [])
                    .filter((e) => e.importable && e.id)
                    .map((e) => String(e.id).trim())
                    .filter(Boolean)
            )];

            // Busca pedidos/transações do período e, em paralelo, txs já vinculadas por autorização.
            // Usa allSettled para não derrubar o matching se um dos lookups falhar.
            const settled = await Promise.allSettled([
                fetchAllPages(api, 'orders', { from: range.from, to: range.to }),
                fetchAllPages(api, 'transactions', { from: range.from, to: range.to }),
                authIds.length
                    ? fetchAllPages(api, 'transactions', { authorization_ids: authIds.join(',') })
                    : Promise.resolve([]),
            ]);

            const orders = settled[0].status === 'fulfilled' ? settled[0].value : [];
            const periodTransactions = settled[1].status === 'fulfilled' ? settled[1].value : [];
            const authTransactions = settled[2].status === 'fulfilled' ? settled[2].value : [];

            const failures = settled
                .filter((result) => result.status === 'rejected')
                .map((result) => result.reason?.response?.data?.message || result.reason?.message)
                .filter(Boolean);
            if (failures.length) {
                setError(failures[0] || 'Falha parcial ao carregar vendas/transações do período.');
            }

            const byId = new Map();
            [...periodTransactions, ...authTransactions].forEach((tx) => {
                if (tx?.id) {
                    byId.set(String(tx.id), tx);
                }
            });
            setRows(buildReconciliationRows(parsedEntries, orders, [...byId.values()]));
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Falha ao carregar vendas/transações do período.');
            setRows(buildReconciliationRows(parsedEntries, [], []));
        } finally {
            setIsLoadingMatches(false);
        }
    };

    const handleFile = (file) => {
        if (!file) {
            return;
        }
        const lower = file.name.toLowerCase();
        if (!lower.endsWith('.csv') && !lower.endsWith('.ofx') && !lower.endsWith('.ofd')) {
            setError('Envie um arquivo CSV, OFX ou OFD.');
            return;
        }

        setIsParsing(true);
        setError('');
        const reader = new FileReader();
        reader.onload = async () => {
            try {
                const text = typeof reader.result === 'string' ? reader.result : '';
                const parsed = parseSettlementFile(file.name, text);
                setFileName(file.name);
                setSourceLabel(parsed.sourceLabel);
                setEntries(parsed.entries);
                if (!parsed.entries.length) {
                    setRows([]);
                    setDateRange(null);
                    return;
                }
                await loadMatches(parsed.entries);
            } catch (err) {
                resetResult();
                setFileName(file.name);
                setError(err?.message || 'Não foi possível ler o arquivo.');
            } finally {
                setIsParsing(false);
                if (inputRef.current) {
                    inputRef.current.value = '';
                }
            }
        };
        reader.onerror = () => {
            setIsParsing(false);
            setError('Falha ao ler o arquivo.');
            if (inputRef.current) {
                inputRef.current.value = '';
            }
        };
        reader.readAsText(file, 'UTF-8');
    };

    const onInputChange = (event) => {
        handleFile(event.target.files?.[0]);
    };

    const onDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();
        handleFile(event.dataTransfer?.files?.[0]);
    };

    const onDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const actionableRows = rows.filter((row) => {
        if (row.status === 'matched_order' || row.status === 'matched_transaction') {
            return row.selected;
        }
        if (row.canImportOrphan && row.importOrphan) {
            return true;
        }
        return false;
    });

    const handleReconcile = async () => {
        if (actionableRows.length === 0) {
            await Swal.fire('Atenção', 'Selecione ao menos um registro para conciliar/importar.', 'info');
            return;
        }

        const confirm = await Swal.fire({
            title: 'Conciliar registros?',
            html: `${actionableRows.length} registro(s) serão vinculados ou importados.<br/><br/>`
                + `Nas transações existentes, a conciliação <strong>atualiza autorização, origem, valor liberado e desconto</strong> com os dados do extrato.<br/>`
                + `Linhas sem pedido criam <strong>apenas a transação</strong> (sem vínculo com venda).`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Conciliar',
            cancelButtonText: 'Cancelar',
        });
        if (!confirm.isConfirmed) {
            return;
        }

        setIsReconciling(true);
        let ok = 0;
        let failed = 0;
        const errors = [];

        for (const row of actionableRows) {
            try {
                // Órfão / sem pedido: cria ou atualiza só a transação, sem order_id.
                const orphanImport = Boolean(row.canImportOrphan && row.importOrphan && !row.order);
                const payload = buildTransactionPayloadFromEntry(
                    row.entry,
                    orphanImport ? '' : (row.order?.id || row.transaction?.order_id || ''),
                    row.outcomeCategory
                );

                if (row.transaction?.id) {
                    // Atualiza autorização e corrige bruto/liberado/desconto a partir do extrato.
                    await api.put(`transactions/${row.transaction.id}`, {
                        ...payload,
                        order_id: orphanImport ? '' : (payload.order_id || row.transaction.order_id || ''),
                        amount: payload.amount,
                        net_amount: payload.net_amount,
                        deduction: payload.deduction,
                        authorization_id: payload.authorization_id,
                        source: payload.source,
                    });
                } else {
                    await api.post('transactions', payload);
                }
                ok += 1;
            } catch (err) {
                failed += 1;
                const apiErrors = err?.response?.data?.errors;
                const detail = apiErrors
                    ? Object.values(apiErrors).join('; ')
                    : (err?.response?.data?.error || err?.response?.data?.message || err?.message || 'erro');
                errors.push(`${row.entry.id || 'linha'}: ${detail}`);
            }
        }

        setIsReconciling(false);

        await Swal.fire({
            title: failed ? 'Conciliação parcial' : 'Conciliação concluída',
            html: `<p>${ok} ok · ${failed} falha(s)</p>${errors.length ? `<p>${errors.slice(0, 3).join('<br/>')}</p>` : ''}`,
            icon: failed ? 'warning' : 'success',
        });

        await loadMatches(entries);
    };

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb">
                <Link color="inherit" onClick={() => navigate('/admin')} underline="hover" sx={{ cursor: 'pointer' }}>
                    Dashboard
                </Link>
                <Link color="inherit" onClick={() => navigate('/admin/transactions')} underline="hover" sx={{ cursor: 'pointer' }}>
                    Transações
                </Link>
                <Typography color="text.primary">Conciliação Bancária</Typography>
            </Breadcrumbs>
            <br />

            <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                    <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
                        Conciliação Bancária
                    </Typography>
                    <PageTutorialButtons pageKey="bank_reconciliation" buttonLabel="Como usar" />
                </Box>
                <Typography variant="body2" color="text.secondary">
                    Importe o extrato (CSV Mercado Pago, OFX ou OFD), localize vendas/transações no período e vincule
                    por tipo de pagamento + valor. Ao vincular, a autorização e a origem são gravadas e o{' '}
                    <strong>liberado/desconto</strong> da transação são corrigidos com o extrato.
                </Typography>
            </Box>

            <QuotaBlockedNotice quotaKey="reconciliations" backTo="/admin/transactions" />

            <Paper
                variant="outlined"
                onDrop={onDrop}
                onDragOver={onDragOver}
                sx={{
                    p: 3,
                    mb: 3,
                    textAlign: 'center',
                    borderStyle: 'dashed',
                    bgcolor: 'action.hover',
                }}
            >
                <CloudUpload sx={{ fontSize: 40, mb: 1, color: 'text.secondary' }} />
                <Typography variant="body1" gutterBottom>
                    Arraste o arquivo aqui ou selecione no computador
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Formatos: CSV, OFX, OFD
                </Typography>
                <input
                    ref={inputRef}
                    type="file"
                    accept={ACCEPTED_EXTENSIONS}
                    hidden
                    onChange={onInputChange}
                />
                <Button
                    variant="contained"
                    disabled={isParsing || isLoadingMatches}
                    onClick={() => inputRef.current?.click()}
                >
                    {isParsing || isLoadingMatches ? 'Processando...' : 'Selecionar arquivo'}
                </Button>
                {fileName ? (
                    <Typography variant="body2" sx={{ mt: 2 }}>
                        Arquivo: <strong>{fileName}</strong>
                        {sourceLabel ? ` · ${sourceLabel}` : ''}
                    </Typography>
                ) : null}
            </Paper>

            {error ? (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            ) : null}

            {fileName && !isParsing && !isLoadingMatches && entries.length === 0 && !error ? (
                <Alert severity="info" sx={{ mb: 3 }}>
                    Não há transações neste arquivo.
                </Alert>
            ) : null}

            {dateRange ? (
                <Alert severity="info" sx={{ mb: 3 }}>
                    Período do arquivo: <strong>{dateRange.fromLabel}</strong> até <strong>{dateRange.toLabel}</strong>
                    {' '}(vendas e transações carregadas neste intervalo).
                </Alert>
            ) : null}

            {entries.length > 0 ? (
                <>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent>
                                    <Typography variant="body2" color="text.secondary">Linhas no arquivo</Typography>
                                    <Typography variant="h5">{summary.count}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {summary.importableCount} importáveis · {summary.skippedCount} sem tipo
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent>
                                    <Typography variant="body2" color="text.secondary">Entradas (liberado)</Typography>
                                    <Typography variant="h5" color="success.main">
                                        {formatCurrencyInFloat(summary.incomeReleased)}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent>
                                    <Typography variant="body2" color="text.secondary">Saídas (liberado)</Typography>
                                    <Typography variant="h5" color="error.main">
                                        {formatCurrencyInFloat(summary.outcomeReleased)}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent>
                                    <Typography variant="body2" color="text.secondary">Descontos / taxas</Typography>
                                    <Typography variant="h5">
                                        {formatCurrencyInFloat(summary.totalDiscount)}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', mb: 2, gap: 1 }}>
                        <FormControlLabel
                            control={(
                                <Switch
                                    checked={hideReconciled}
                                    onChange={(e) => setHideReconciled(e.target.checked)}
                                    color="primary"
                                />
                            )}
                            label={
                                hideReconciled && reconciledCount > 0
                                    ? `Ocultar já conciliados (${reconciledCount})`
                                    : 'Ocultar já conciliados'
                            }
                        />
                        <Button
                            variant="contained"
                            disabled={isReconciling || isLoadingMatches || actionableRows.length === 0 || quotaBlocked}
                            onClick={handleReconcile}
                            startIcon={isReconciling ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                            {isReconciling ? 'Conciliando...' : `Conciliar selecionados (${actionableRows.length})`}
                        </Button>
                    </Box>

                    <SettlementSection
                        title="Entradas identificáveis"
                        subtitle="Recebimentos com tipo de pagamento no extrato"
                        rows={rowGroups.income}
                        emptyText="Nenhuma entrada identificável neste arquivo."
                        updateRow={updateRow}
                    />
                    <SettlementSection
                        title="Saídas identificáveis"
                        subtitle="Saídas com tipo de pagamento — categorize como Despesa ou Retirada (ambas baixam o saldo)"
                        rows={rowGroups.outcome}
                        emptyText="Nenhuma saída identificável neste arquivo."
                        updateRow={updateRow}
                        showOutcomeCategory
                        sx={{ mt: 3 }}
                    />
                    <SettlementSection
                        title="Não identificados"
                        subtitle="Linhas sem tipo de pagamento — não entram na conciliação automática"
                        rows={rowGroups.unidentified}
                        emptyText="Nenhuma linha sem tipo neste arquivo."
                        updateRow={updateRow}
                        sx={{ mt: 3 }}
                    />
                </>
            ) : null}
        </div>
    );
}

function SettlementSection({ title, subtitle, rows, emptyText, updateRow, showOutcomeCategory, sx }) {
    const setAllOutcomeCategory = (category) => {
        rows.forEach((row) => {
            if (row.entry?.direction === 'outcome') {
                updateRow(row.rowKey, { outcomeCategory: category });
            }
        });
    };

    return (
        <Box sx={sx}>
            <Box sx={{ mb: 1.5, display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 2 }}>
                <Box sx={{ flex: 1, minWidth: 220 }}>
                    <Typography variant="h6" component="h2">
                        {title}
                        <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                            ({rows.length})
                        </Typography>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {subtitle}
                    </Typography>
                </Box>
                {showOutcomeCategory && rows.length > 0 ? (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button size="small" variant="outlined" onClick={() => setAllOutcomeCategory('expense')}>
                            Todas despesa
                        </Button>
                        <Button size="small" variant="outlined" onClick={() => setAllOutcomeCategory('withdrawal')}>
                            Todas retirada
                        </Button>
                    </Box>
                ) : null}
            </Box>
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Sel.</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Data</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Fluxo</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Pagamento</TableCell>
                            {showOutcomeCategory ? (
                                <TableCell sx={{ fontWeight: 'bold' }}>Categoria</TableCell>
                            ) : null}
                            <TableCell sx={{ fontWeight: 'bold' }} align="right">Cobrado</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }} align="right">Desconto</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }} align="right">Liberado</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Situação</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Pedido / Transação</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={showOutcomeCategory ? 11 : 10}>
                                    <Typography variant="body2" color="text.secondary">
                                        {emptyText}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            rows.map((row) => (
                                <SettlementRow
                                    key={row.rowKey}
                                    row={row}
                                    updateRow={updateRow}
                                    showOutcomeCategory={showOutcomeCategory}
                                />
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}

function SettlementRow({ row, updateRow, showOutcomeCategory }) {
    return (
        <TableRow>
            <TableCell>
                {row.status === 'matched_order' || row.status === 'matched_transaction' ? (
                    <Checkbox
                        checked={Boolean(row.selected)}
                        onChange={(e) => updateRow(row.rowKey, { selected: e.target.checked })}
                        size="small"
                    />
                ) : null}
                {row.canImportOrphan ? (
                    <FormControlLabel
                        control={(
                            <Checkbox
                                checked={Boolean(row.importOrphan)}
                                onChange={(e) => updateRow(row.rowKey, { importOrphan: e.target.checked })}
                                size="small"
                            />
                        )}
                        label={<Typography variant="caption">Só tx</Typography>}
                    />
                ) : null}
            </TableCell>
            <TableCell>{formatSettlementDate(row.entry.transactionDate)}</TableCell>
            <TableCell>
                <Chip
                    size="small"
                    icon={row.entry.direction === 'income' ? <TrendingUp /> : <TrendingDown />}
                    label={row.entry.directionLabel}
                    color={row.entry.direction === 'income' ? 'success' : 'error'}
                    variant="outlined"
                />
            </TableCell>
            <TableCell>{row.entry.paymentMethodLabel || '—'}</TableCell>
            {showOutcomeCategory ? (
                <TableCell sx={{ minWidth: 140 }}>
                    <FormControl size="small" fullWidth>
                        <InputLabel id={`cat-${row.rowKey}`}>Categoria</InputLabel>
                        <Select
                            labelId={`cat-${row.rowKey}`}
                            label="Categoria"
                            value={row.outcomeCategory === 'withdrawal' ? 'withdrawal' : 'expense'}
                            onChange={(e) => updateRow(row.rowKey, { outcomeCategory: e.target.value })}
                            disabled={row.status === 'already_reconciled'}
                        >
                            <MenuItem value="expense">Despesa</MenuItem>
                            <MenuItem value="withdrawal">Retirada</MenuItem>
                        </Select>
                    </FormControl>
                </TableCell>
            ) : null}
            <TableCell align="right">{formatCurrencyInFloat(row.entry.chargedAmount)}</TableCell>
            <TableCell align="right">
                {row.entry.discountAmount
                    ? formatCurrencyInFloat(row.entry.discountAmount)
                    : '—'}
            </TableCell>
            <TableCell align="right">{formatCurrencyInFloat(row.entry.releasedAmount)}</TableCell>
            <TableCell>
                <Chip size="small" color={statusColor(row.status)} label={row.statusLabel} />
            </TableCell>
            <TableCell>
                {row.order ? (
                    <Typography variant="body2">
                        Pedido {String(row.order.id).slice(0, 8)}…
                        {' · '}
                        {formatCurrencyInCents(row.order.amount)}
                    </Typography>
                ) : null}
                {row.transaction ? (
                    <Typography variant="caption" display="block">
                        Tx {String(row.transaction.id).slice(0, 8)}…
                    </Typography>
                ) : null}
                {!row.order && !row.transaction && (row.status === 'no_order' || row.status === 'orphan') ? (
                    <Typography variant="body2" color="text.secondary">
                        Sem pedido — só transação do extrato
                    </Typography>
                ) : null}
            </TableCell>
            <TableCell>
                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                    {row.entry.id || '—'}
                </Typography>
            </TableCell>
        </TableRow>
    );
}

export default SettlementImportPage;
